import { PageLoader } from '@/components/ui/loader';
import { AiConversationTools, AiRewriteMenu } from '@/features/ai/ai';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  Clock,
  Globe,
  Inbox as InboxIcon,
  MessageCircle,
  RotateCcw,
  Search,
  Send,
  Settings2,
  StickyNote,
  UserPlus,
} from 'lucide-react';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, Select, Textarea } from '@/components/ui/misc';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import {
  useAssignConversation,
  useConversation as useWhatsappConversation,
  useSendWhatsapp,
} from '@/features/vault/api';
import {
  KIND_COLOURS,
  KIND_LABELS,
  useConversation,
  useConversationBot,
  useConversationLead,
  useConversations,
  useReply,
  useSalesbots,
  useUpdateConversation,
  type ConversationKind,
  type ConversationRow,
} from './api';

const FILTERS = [
  ['all', 'All'],
  ['me', 'Mine'],
  ['unassigned', 'Unassigned'],
] as const;

/**
 * The unified inbox (AmoCRM): website chat, WhatsApp, Telegram, Messenger
 * and Instagram in one list, newest first, each tied to its lead.
 */
export function InboxPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c') ?? undefined;
  const selectedSource = (params.get('source') as 'whatsapp' | null) ?? 'inbox';

  const [assigned, setAssigned] = useState<(typeof FILTERS)[number][0]>('all');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);

  const { data: rows = [], isLoading } = useConversations({
    assigned: assigned === 'all' ? undefined : assigned,
    kind: kind || undefined,
    status: status || undefined,
    q: debounced || undefined,
  });

  const select = (row: ConversationRow) =>
    setParams(row.source === 'whatsapp' ? { c: row.id, source: 'whatsapp' } : { c: row.id });

  return (
    <div className="flex h-full overflow-hidden bg-background">
      {/* The list */}
      <aside className={cn('flex w-full flex-col border-e lg:w-80 xl:w-96', selectedId && 'hidden lg:flex')}>
        <div className="space-y-2 border-b p-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold tracking-tight">Inbox</h1>
            <Can permission="inbox.channel.view">
              <Button asChild variant="ghost" size="sm" className="ms-auto h-8 px-2">
                <Link to="/inbox/channels" title="Channels">
                  <Settings2 className="h-4 w-4" />
                </Link>
              </Button>
            </Can>
            <Can permission="inbox.salesbot.view">
              <Button asChild variant="ghost" size="sm" className="h-8 px-2">
                <Link to="/inbox/salesbots" title="Salesbots">
                  <Bot className="h-4 w-4" />
                </Link>
              </Button>
            </Can>
          </div>
          <div className="relative">
            <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search conversations…"
              className="h-9 ps-8"
            />
          </div>
          <div className="flex gap-1">
            {FILTERS.map(([value, label]) => (
              <button
                key={value}
                onClick={() => setAssigned(value)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  assigned === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Select value={kind} onChange={(event) => setKind(event.target.value)} className="h-8 flex-1 text-xs">
              <option value="">Every channel</option>
              {(Object.keys(KIND_LABELS) as ConversationKind[]).map((value) => (
                <option key={value} value={value}>
                  {KIND_LABELS[value]}
                </option>
              ))}
            </Select>
            <Select value={status} onChange={(event) => setStatus(event.target.value)} className="h-8 flex-1 text-xs">
              <option value="">Open</option>
              <option value="RESOLVED">Resolved</option>
              <option value="ALL">Everything</option>
            </Select>
          </div>
        </div>

        <div className="scrollbar-thin flex-1 overflow-y-auto">
          {isLoading && <PageLoader />}
          {!isLoading && rows.length === 0 && (
            <div className="p-8 text-center">
              <InboxIcon className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">Nothing here</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Connect the website chat, Telegram, Messenger or Instagram under Channels.
              </p>
            </div>
          )}
          {rows.map((row) => (
            <button
              key={`${row.source}-${row.id}`}
              onClick={() => select(row)}
              className={cn(
                'flex w-full items-start gap-3 border-b px-3 py-3 text-start transition-colors hover:bg-muted/50',
                row.id === selectedId && 'bg-primary/5 hover:bg-primary/10',
              )}
            >
              <div className="relative shrink-0">
                <Avatar name={row.name} src={row.avatarUrl} size={40} />
                <span
                  className="absolute -bottom-0.5 -end-0.5 h-4 w-4 rounded-full border-2 border-background"
                  style={{ backgroundColor: KIND_COLOURS[row.kind] }}
                  title={KIND_LABELS[row.kind]}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={cn('truncate text-sm', row.unreadCount ? 'font-bold' : 'font-medium')}>{row.name}</span>
                  <span className="ms-auto shrink-0 text-[10px] text-muted-foreground">{timeAgo(row.lastMessageAt)}</span>
                </div>
                <p className={cn('mt-0.5 truncate text-xs', row.unreadCount ? 'text-foreground' : 'text-muted-foreground')}>
                  {row.preview || '—'}
                </p>
                <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span>{KIND_LABELS[row.kind]}</span>
                  {row.botActive && (
                    <span className="flex items-center gap-0.5 text-violet-600">
                      <Bot className="h-3 w-3" /> bot
                    </span>
                  )}
                  {row.lead?.isUnsorted && <span className="rounded bg-amber-100 px-1 text-amber-700">Incoming</span>}
                  {row.overdue ? (
                    <span className="flex items-center gap-0.5 rounded bg-red-100 px-1 font-semibold text-red-700">
                      <Clock className="h-3 w-3" /> Overdue
                    </span>
                  ) : row.replyDueAt ? (
                    <span className="flex items-center gap-0.5 text-amber-600" title="Reply due">
                      <Clock className="h-3 w-3" /> {timeLeft(row.replyDueAt)}
                    </span>
                  ) : null}
                  {row.assignedTo && <span className="truncate">· {row.assignedTo.name}</span>}
                  {row.unreadCount > 0 && (
                    <span className="ms-auto rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                      {row.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* The thread */}
      <section className={cn('min-w-0 flex-1', !selectedId && 'hidden lg:block')}>
        {!selectedId ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 opacity-30" />
            <p className="mt-3 text-sm">Choose a conversation</p>
          </div>
        ) : selectedSource === 'whatsapp' ? (
          <WhatsappThread id={selectedId} onBack={() => setParams({})} />
        ) : (
          <InboxThread id={selectedId} onBack={() => setParams({})} />
        )}
      </section>
    </div>
  );
}

// ── Inbox conversation ───────────────────────────────────────────────────────

function InboxThread({ id, onBack }: { id: string; onBack: () => void }) {
  const { data: conversation, error } = useConversation(id);
  const reply = useReply(id);
  const update = useUpdateConversation(id);
  const makeLead = useConversationLead(id);
  const bot = useConversationBot(id);
  const { data: users } = useUserOptions();
  const { data: bots } = useSalesbots();
  const [note, setNote] = useState(false);

  if (error) return <p className="p-6 text-sm text-destructive">Could not load this conversation.</p>;
  if (!conversation) return <PageLoader />;

  const name = conversation.displayName || conversation.email || conversation.phone || 'Visitor';
  const kind = conversation.channel.kind;

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <ThreadHeader
          name={name}
          kind={kind}
          subtitle={conversation.channel.name}
          onBack={onBack}
          resolved={conversation.status === 'RESOLVED'}
          onResolve={() => update.mutate({ status: conversation.status === 'RESOLVED' ? 'OPEN' : 'RESOLVED' })}
        />

        <Messages
          items={conversation.messages.map((message) => ({
            id: message.id,
            mine: message.direction === 'OUTBOUND',
            note: message.isInternalNote,
            bot: message.isBot,
            body: message.body,
            buttons: message.buttons,
            author: message.isBot ? 'Salesbot' : message.sentBy?.name ?? null,
            at: message.createdAt,
            failed: message.status === 'FAILED' ? message.errorMessage ?? 'Not delivered' : null,
            attachments: message.attachments,
          }))}
        />

        {conversation.bot && (
          <div className="flex items-center gap-2 border-t bg-violet-50 px-4 py-2 text-xs text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            <Bot className="h-4 w-4" />
            The “{conversation.bot.name}” Salesbot is talking. Replying yourself stops it.
            <button className="ms-auto font-medium underline" onClick={() => bot.mutate(null)}>
              Stop bot
            </button>
          </div>
        )}

        <Can permission="inbox.conversation.create">
          <Composer
            note={note}
            onNoteChange={setNote}
            pending={reply.isPending}
            error={reply.error ? (reply.error as ApiRequestError).message : null}
            onSend={(body) => reply.mutateAsync({ body, isInternalNote: note })}
            ai={{ source: 'inbox', id: conversation.id, leadId: conversation.lead?.id ?? null }}
          />
        </Can>
      </div>

      {/* The customer */}
      <aside className="scrollbar-thin hidden w-72 shrink-0 space-y-5 overflow-y-auto border-s p-4 xl:block">
        <div className="text-center">
          <Avatar name={name} src={conversation.avatarUrl} size={64} className="mx-auto" />
          <p className="mt-2 font-semibold">{name}</p>
          <p className="text-xs" style={{ color: KIND_COLOURS[kind] }}>
            {KIND_LABELS[kind]}
          </p>
        </div>

        <dl className="space-y-1.5 text-sm">
          {conversation.email && <Detail label="Email" value={conversation.email} />}
          {conversation.phone && <Detail label="Phone" value={conversation.phone} />}
          {conversation.context?.username && <Detail label="Username" value={`@${conversation.context.username}`} />}
          {conversation.context?.subject && <Detail label="Subject" value={conversation.context.subject} />}
          <Detail label="Started" value={formatDateTime(conversation.createdAt)} />
        </dl>

        {conversation.context?.pageUrl && (
          <div className="rounded-lg bg-muted/50 p-2.5 text-xs">
            <p className="flex items-center gap-1 font-medium">
              <Globe className="h-3.5 w-3.5" /> Chatting from
            </p>
            <p className="mt-1 break-all text-muted-foreground">{conversation.context.pageUrl}</p>
            {Object.entries(conversation.context.utm ?? {}).map(([key, value]) => (
              <p key={key} className="text-muted-foreground">
                {key}: {value}
              </p>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lead</p>
          {conversation.lead ? (
            <Link
              to={conversation.lead.isUnsorted ? '/crm/leads/board' : `/crm/leads?q=${conversation.lead.number}`}
              className="block rounded-lg border p-2.5 text-sm hover:bg-muted/50"
            >
              <span className="font-medium">
                {conversation.lead.firstName} {conversation.lead.lastName ?? ''}
              </span>
              <span className="block text-xs text-muted-foreground">
                {conversation.lead.number}
                {conversation.lead.isUnsorted ? ' · waiting in Incoming' : ''}
              </span>
            </Link>
          ) : conversation.contact ? (
            <p className="rounded-lg border p-2.5 text-sm">
              Contact: {conversation.contact.firstName} {conversation.contact.lastName ?? ''}
            </p>
          ) : (
            <Can permission="crm.lead.create">
              <Button size="sm" variant="outline" className="w-full" disabled={makeLead.isPending} onClick={() => makeLead.mutate()}>
                <UserPlus className="h-4 w-4" />
                Create lead
              </Button>
            </Can>
          )}
        </div>

        <Can permission="inbox.conversation.update">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assigned to</p>
            <Select
              value={conversation.assignedTo?.id ?? ''}
              onChange={(event) => update.mutate({ assignedToId: event.target.value || null })}
              className="h-9 text-sm"
            >
              <option value="">Nobody</option>
              {(users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>

          {!conversation.bot && (bots ?? []).some((item) => item.status === 'ACTIVE') && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Salesbot</p>
              <Select value="" onChange={(event) => event.target.value && bot.mutate(event.target.value)} className="h-9 text-sm">
                <option value="">Start a bot…</option>
                {(bots ?? [])
                  .filter((item) => item.status === 'ACTIVE')
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Select>
            </div>
          )}
        </Can>
      </aside>
    </div>
  );
}

// ── WhatsApp conversation, answered through the WhatsApp module ──────────────

function WhatsappThread({ id, onBack }: { id: string; onBack: () => void }) {
  const { data: conversation } = useWhatsappConversation(id);
  const send = useSendWhatsapp(id);
  const assign = useAssignConversation(id);
  const { data: users } = useUserOptions();
  const [note, setNote] = useState(false);

  if (!conversation) return <PageLoader />;
  const name = conversation.displayName || conversation.phone;

  return (
    <div className="flex h-full flex-col">
      <ThreadHeader name={name} kind="WHATSAPP" subtitle={`${conversation.account.label} · ${conversation.phone}`} onBack={onBack} />
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-xs">
        <span className={cn('flex items-center gap-1', conversation.sessionOpen ? 'text-emerald-600' : 'text-amber-600')}>
          <Clock className="h-3.5 w-3.5" />
          {conversation.sessionOpen
            ? `Reply window open · ${Math.floor(conversation.sessionMinutesLeft / 60)}h ${conversation.sessionMinutesLeft % 60}m left`
            : 'Window closed — only templates (from the WhatsApp page)'}
        </span>
        <Select
          value={conversation.assignedTo?.id ?? ''}
          onChange={(event) => assign.mutate(event.target.value || null)}
          className="ms-auto h-7 w-auto text-xs"
        >
          <option value="">Unassigned</option>
          {(users ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </Select>
      </div>
      <Messages
        items={(conversation.messages ?? []).map((message) => ({
          id: message.id,
          mine: message.direction === 'OUTBOUND',
          note: message.isInternalNote,
          bot: !message.sentBy && message.direction === 'OUTBOUND' && !message.template,
          body: message.template ? `Template: ${message.template.name}` : message.body,
          buttons: [],
          author: message.sentBy?.name ?? null,
          at: message.createdAt,
          failed: message.status === 'FAILED' ? message.errorMessage ?? 'Not delivered' : null,
          attachments: null,
        }))}
      />
      <Can permission="marketing.campaign.view">
        <Composer
          note={note}
          onNoteChange={setNote}
          disabled={!conversation.sessionOpen && !note}
          pending={send.isPending}
          error={send.error ? (send.error as ApiRequestError).message : null}
          onSend={(body) => send.mutateAsync({ body, isInternalNote: note })}
          ai={{ source: 'whatsapp', id: conversation.id }}
        />
      </Can>
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

function ThreadHeader({
  name,
  kind,
  subtitle,
  onBack,
  resolved,
  onResolve,
}: {
  name: string;
  kind: ConversationKind;
  subtitle: string;
  onBack: () => void;
  resolved?: boolean;
  onResolve?: () => void;
}) {
  return (
    <div className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
      <button onClick={onBack} className="lg:hidden" aria-label="Back">
        <ArrowLeft className="h-5 w-5" />
      </button>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: KIND_COLOURS[kind] }} />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {KIND_LABELS[kind]} · {subtitle}
        </p>
      </div>
      {onResolve && (
        <Button size="sm" variant={resolved ? 'outline' : 'default'} className="ms-auto" onClick={onResolve}>
          {resolved ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {resolved ? 'Reopen' : 'Resolve'}
        </Button>
      )}
    </div>
  );
}

type Item = {
  id: string;
  mine: boolean;
  note: boolean;
  bot: boolean;
  body: string | null;
  buttons: string[];
  author: string | null;
  at: string;
  failed: string | null;
  attachments: { type: string; url?: string; name?: string }[] | null;
};

function Messages({ items }: { items: Item[] }) {
  const end = useRef<HTMLDivElement>(null);
  const last = items[items.length - 1]?.id;
  useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [last]);

  return (
    <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto bg-muted/20 p-4">
      {items.map((item) => (
        <div key={item.id} className={cn('flex', item.mine ? 'justify-end' : 'justify-start')}>
          <div
            className={cn(
              'max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm',
              item.note
                ? 'border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                : item.mine
                  ? item.bot
                    ? 'rounded-ee-md bg-violet-600 text-white'
                    : 'rounded-ee-md bg-primary text-primary-foreground'
                  : 'rounded-es-md border bg-background',
            )}
          >
            {(item.note || item.author || item.bot) && (
              <p className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold opacity-75">
                {item.note && <StickyNote className="h-3 w-3" />}
                {item.bot && !item.note && <Bot className="h-3 w-3" />}
                {item.note ? `Internal note${item.author ? ` · ${item.author}` : ''}` : item.author ?? 'Salesbot'}
              </p>
            )}
            {item.body && <p className="whitespace-pre-wrap break-words">{item.body}</p>}
            {item.attachments?.map((attachment, index) =>
              attachment.url ? (
                <a key={index} href={attachment.url} target="_blank" rel="noreferrer" className="block text-xs underline">
                  {attachment.name ?? attachment.type}
                </a>
              ) : (
                <p key={index} className="text-xs italic opacity-75">
                  [{attachment.name ?? attachment.type}]
                </p>
              ),
            )}
            {item.buttons.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {item.buttons.map((button) => (
                  <span key={button} className="rounded-full border border-current/30 bg-white/15 px-2 py-0.5 text-[11px]">
                    {button}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-0.5 text-end text-[10px] opacity-60">{timeAgo(item.at)}</p>
            {item.failed && <p className="mt-0.5 text-[11px] font-medium text-red-200">⚠ {item.failed}</p>}
          </div>
        </div>
      ))}
      <div ref={end} />
    </div>
  );
}

function Composer({
  note,
  onNoteChange,
  onSend,
  pending,
  error,
  disabled,
  ai,
}: {
  note: boolean;
  onNoteChange: (note: boolean) => void;
  onSend: (body: string) => Promise<unknown>;
  pending: boolean;
  error: string | null;
  disabled?: boolean;
  ai?: { source: 'inbox' | 'whatsapp'; id: string; leadId?: string | null };
}) {
  const [body, setBody] = useState('');

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    try {
      await onSend(text);
      setBody('');
    } catch {
      // The error is shown under the box; the text stays so it can be retried.
    }
  };

  return (
    <div className={cn('relative border-t p-3', note && 'bg-amber-50/60 dark:bg-amber-950/20')}>
      <div className="mb-2 flex flex-wrap gap-1 text-xs">
        <button
          onClick={() => onNoteChange(false)}
          className={cn('rounded-md px-2.5 py-1', !note ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground')}
        >
          Reply
        </button>
        <button
          onClick={() => onNoteChange(true)}
          className={cn('rounded-md px-2.5 py-1', note ? 'bg-amber-200/60 font-medium text-amber-800' : 'text-muted-foreground')}
        >
          Internal note
        </button>
        {ai && !note && <AiConversationTools source={ai.source} id={ai.id} leadId={ai.leadId} onReply={setBody} />}
      </div>
      <div className="flex items-end gap-2">
        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
          rows={2}
          disabled={disabled}
          placeholder={disabled ? 'The reply window is closed' : note ? 'Only your team sees this…' : 'Type a reply — Enter to send'}
          className="min-h-[2.75rem] flex-1 resize-none"
        />
        <AiRewriteMenu text={body} onResult={setBody} size="icon" />
        <Button onClick={() => void submit()} disabled={pending || disabled || !body.trim()} className="h-11">
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** "12m", "2h" — what is left before a reply is due. */
function timeLeft(due: string): string {
  const minutes = Math.max(0, Math.round((new Date(due).getTime() - Date.now()) / 60_000));
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h` : `${minutes}m`;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="truncate text-end">{value}</dd>
    </div>
  );
}
